import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/useDebounce';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  CrmContactRow,
  CrmFieldDefinition,
  CrmFilterRule,
  CrmSortRule,
  CrmViewConfig,
  Task,
  TaskPriority,
} from '@callhub/shared';
import { Button, Input } from '@/components/ui-next';
import { api } from '@/lib/api';
import { buildDialerPath } from '@/lib/routes';

const STANDARD_COLUMNS = [
  { key: 'displayName', label: 'Name' },
  { key: 'phoneNumber', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'stageLabel', label: 'Stage' },
  { key: 'decayLevel', label: 'Decay' },
  { key: 'lastOutboundAt', label: 'Last Outbound' },
] as const;

const DEFAULT_COLUMNS = ['displayName', 'phoneNumber', 'email', 'stageLabel'];

type ViewMode = 'list' | 'board';
type DetailTab = 'profile' | 'fields' | 'tasks';

type CustomFieldDraft = Record<string, string>;

function areStringArraysEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
}

function areDraftsEqual(left: CustomFieldDraft, right: CustomFieldDraft): boolean {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  for (const key of leftKeys) {
    if (left[key] !== right[key]) return false;
  }
  return true;
}

function formatDateLabel(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString();
}

function normalizeColumnToken(input: string, fields: CrmFieldDefinition[]): string {
  if (input.startsWith('cf:')) return input;
  const matchingField = fields.find((field) => field.key === input);
  if (matchingField) return `cf:${matchingField.id}`;
  return input;
}

function resolveColumnLabel(column: string, fields: CrmFieldDefinition[]): string {
  const standard = STANDARD_COLUMNS.find((item) => item.key === column);
  if (standard) return standard.label;
  if (column.startsWith('cf:')) {
    const field = fields.find((item) => `cf:${item.id}` === column);
    return field?.label ?? column;
  }
  return column;
}

function getCustomFieldValue(row: CrmContactRow, field: CrmFieldDefinition): string {
  const raw = row.customFields?.[field.key];
  if (raw === null || raw === undefined) return '—';
  if (Array.isArray(raw)) return raw.join(', ');
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
  if (typeof raw === 'object') return JSON.stringify(raw);
  return String(raw);
}

function getCellValue(row: CrmContactRow, column: string, fields: CrmFieldDefinition[]): string {
  if (column === 'displayName') return row.displayName ?? row.phoneNumber;
  if (column === 'phoneNumber') return row.phoneNumber;
  if (column === 'email') return row.email ?? '—';
  if (column === 'stageLabel') return row.stageLabel ?? '—';
  if (column === 'decayLevel') return row.decayLevel;
  if (column === 'lastOutboundAt') return formatDateLabel(row.lastOutboundAt ?? null);
  if (column.startsWith('cf:')) {
    const field = fields.find((item) => `cf:${item.id}` === column);
    if (!field) return '—';
    return getCustomFieldValue(row, field);
  }
  return '—';
}

function getDecayBadgeClass(level: CrmContactRow['decayLevel']) {
  if (level === 'critical') {
    return 'bg-red-500/15 text-red-700 dark:text-red-300';
  }
  if (level === 'warning') {
    return 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
  }
  return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
}

function buildViewConfig(input: {
  columns: string[];
  filters: CrmFilterRule[];
  sorts: CrmSortRule[];
  pipelineId: string | null;
  viewMode: ViewMode;
}): CrmViewConfig {
  return {
    columns: input.columns,
    filters: input.filters,
    sorts: input.sorts,
    pipelineId: input.pipelineId,
    viewMode: input.viewMode,
  };
}

export function CrmContactsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_COLUMNS);
  const [sortField, setSortField] = useState<string>('updatedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filterField, setFilterField] = useState<string>('stageLabel');
  const [filterOperator, setFilterOperator] = useState<CrmFilterRule['operator']>('eq');
  const [filterValue, setFilterValue] = useState('');
  const debouncedFilterValue = useDebounce(filterValue, 300);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [newViewName, setNewViewName] = useState('');
  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('profile');
  const [customFieldDrafts, setCustomFieldDrafts] = useState<CustomFieldDraft>({});
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState<TaskPriority>('medium');
  const [newTaskDueAt, setNewTaskDueAt] = useState('');

  const selectedContactIdFromQuery = searchParams.get('contact');

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const response = await api.getMe();
      return response.data;
    },
  });

  const fieldsQuery = useQuery({
    queryKey: ['crm-fields'],
    queryFn: async () => {
      const response = await api.getCrmFields();
      return response.data;
    },
    retry: false,
  });

  const viewsQuery = useQuery({
    queryKey: ['crm-views'],
    queryFn: async () => {
      const response = await api.getCrmViews();
      return response.data;
    },
    retry: false,
  });

  const pipelinesQuery = useQuery({
    queryKey: ['crm-pipelines'],
    queryFn: async () => {
      const response = await api.getCrmPipelines();
      return response.data;
    },
    retry: false,
  });

  const taskStatusesQuery = useQuery({
    queryKey: ['crm-task-statuses'],
    queryFn: async () => {
      const response = await api.getTaskStatuses();
      return response.data;
    },
    retry: false,
  });

  const filters = useMemo<CrmFilterRule[]>(() => {
    const rules: CrmFilterRule[] = [];
    if (debouncedFilterValue.trim()) {
      rules.push({
        field: filterField,
        operator: filterOperator,
        value: debouncedFilterValue.trim(),
      });
    }
    if (debouncedSearch.trim()) {
      rules.push({
        field: 'displayName',
        operator: 'contains',
        value: debouncedSearch.trim(),
      });
    }
    return rules;
  }, [debouncedFilterValue, debouncedSearch, filterField, filterOperator]);

  const sorts = useMemo<CrmSortRule[]>(
    () => [{ field: sortField, direction: sortDirection }],
    [sortDirection, sortField],
  );

  const contactsQuery = useQuery({
    queryKey: ['crm-contacts-query', pipelineId, filters, sorts],
    queryFn: async () => {
      const response = await api.queryCrmContacts({
        pipelineId,
        filters,
        sorts,
        columns: visibleColumns,
      });
      return response.data;
    },
    retry: false,
    enabled: fieldsQuery.isSuccess && pipelinesQuery.isSuccess,
  });

  const rows = useMemo(() => contactsQuery.data?.rows ?? [], [contactsQuery.data?.rows]);
  const board = useMemo(() => contactsQuery.data?.board ?? [], [contactsQuery.data?.board]);
  const fields = useMemo(() => fieldsQuery.data ?? [], [fieldsQuery.data]);
  const views = useMemo(() => viewsQuery.data ?? [], [viewsQuery.data]);
  const taskStatuses = useMemo(() => taskStatusesQuery.data ?? [], [taskStatusesQuery.data]);
  const pipelines = useMemo(() => pipelinesQuery.data ?? [], [pipelinesQuery.data]);

  const selectedContactId = selectedContactIdFromQuery;
  const selectedContact = useMemo<CrmContactRow | null>(() => {
    if (!selectedContactId) return rows[0] ?? null;
    return rows.find((row) => row.id === selectedContactId) ?? null;
  }, [rows, selectedContactId]);

  const selectedContactTasksQuery = useQuery({
    queryKey: ['crm-contact-tasks', selectedContact?.id],
    queryFn: async () => {
      const response = await api.getTasks({
        contactId: selectedContact!.id,
        includeCompleted: true,
      });
      return response.data;
    },
    enabled: Boolean(selectedContact?.id),
    retry: false,
  });

  useEffect(() => {
    if (!pipelines.length) return;
    if (pipelineId && pipelines.some((pipeline) => pipeline.id === pipelineId)) return;
    const defaultPipeline = pipelines.find((pipeline) => pipeline.isDefault) ?? pipelines[0];
    setPipelineId(defaultPipeline?.id ?? null);
  }, [pipelineId, pipelines]);

  useEffect(() => {
    if (!views.length) return;
    if (activeViewId && views.some((view) => view.id === activeViewId)) return;
    const defaultView = views.find((view) => view.isDefault) ?? views[0];
    setActiveViewId(defaultView?.id ?? null);
  }, [activeViewId, views]);

  useEffect(() => {
    if (!activeViewId) return;
    const activeView = views.find((view) => view.id === activeViewId);
    if (!activeView) return;
    const config = activeView.config;

    if (config.columns?.length) {
      const nextColumns = config.columns.map((column) => normalizeColumnToken(column, fields));
      setVisibleColumns((current) =>
        areStringArraysEqual(current, nextColumns) ? current : nextColumns,
      );
    }
    if (config.sorts?.[0]) {
      const nextSort = config.sorts[0];
      setSortField((current) => (current === nextSort.field ? current : nextSort.field));
      setSortDirection((current) =>
        current === nextSort.direction ? current : nextSort.direction,
      );
    }
    if (config.filters?.[0]) {
      const nextFilter = config.filters[0];
      const nextFilterValue = String(nextFilter.value ?? '');
      setFilterField((current) => (current === nextFilter.field ? current : nextFilter.field));
      setFilterOperator((current) =>
        current === nextFilter.operator ? current : nextFilter.operator,
      );
      setFilterValue((current) => (current === nextFilterValue ? current : nextFilterValue));
    } else {
      setFilterValue((current) => (current === '' ? current : ''));
    }
    if (config.pipelineId !== undefined) {
      const nextPipelineId = config.pipelineId ?? null;
      setPipelineId((current) => (current === nextPipelineId ? current : nextPipelineId));
    }
    if (config.viewMode === 'list' || config.viewMode === 'board') {
      const nextViewMode: ViewMode = config.viewMode;
      setViewMode((current) => (current === nextViewMode ? current : nextViewMode));
    }
  }, [activeViewId, fields, views]);

  useEffect(() => {
    const selected = selectedContact ?? rows[0] ?? null;
    if (!selected) return;
    const nextParams = new URLSearchParams(searchParams);
    if (nextParams.get('contact') === selected.id) return;
    nextParams.set('contact', selected.id);
    setSearchParams(nextParams, { replace: true });
  }, [rows, searchParams, selectedContact, setSearchParams]);

  useEffect(() => {
    if (!selectedContact) {
      setCustomFieldDrafts((current) => (Object.keys(current).length > 0 ? {} : current));
      return;
    }
    const nextDrafts: CustomFieldDraft = {};
    for (const field of fields) {
      const raw = selectedContact.customFields?.[field.key];
      if (raw === null || raw === undefined) {
        nextDrafts[field.id] = '';
      } else if (Array.isArray(raw)) {
        nextDrafts[field.id] = raw.join(', ');
      } else if (typeof raw === 'object') {
        nextDrafts[field.id] = JSON.stringify(raw);
      } else {
        nextDrafts[field.id] = String(raw);
      }
    }
    setCustomFieldDrafts((current) => (areDraftsEqual(current, nextDrafts) ? current : nextDrafts));
  }, [fields, selectedContact]);

  const saveViewMutation = useMutation({
    mutationFn: async (payload: { mode: 'create' | 'update'; name?: string }) => {
      const config = buildViewConfig({
        columns: visibleColumns,
        filters,
        sorts,
        pipelineId,
        viewMode,
      });
      if (payload.mode === 'create') {
        const name = payload.name?.trim();
        if (!name) throw new Error('View name is required');
        return api.createCrmView({ name, config });
      }
      if (!activeViewId) throw new Error('No active view selected');
      return api.updateCrmView(activeViewId, { config });
    },
    onSuccess: async () => {
      setNewViewName('');
      await queryClient.invalidateQueries({ queryKey: ['crm-views'] });
    },
  });

  const deleteViewMutation = useMutation({
    mutationFn: async () => {
      if (!activeViewId) throw new Error('No active view selected');
      return api.deleteCrmView(activeViewId);
    },
    onSuccess: async () => {
      setActiveViewId(null);
      await queryClient.invalidateQueries({ queryKey: ['crm-views'] });
    },
  });

  const updateFieldsMutation = useMutation({
    mutationFn: async () => {
      if (!selectedContact) throw new Error('No contact selected');
      const values = fields.map((field) => {
        const raw = (customFieldDrafts[field.id] ?? '').trim();
        const base = {
          definitionId: field.id,
          valueText: null as string | null,
          valueNumber: null as number | null,
          valueDate: null as string | null,
          valueBoolean: null as boolean | null,
          valueJson: null as string | null,
        };
        if (!raw) return base;

        if (field.fieldType === 'number' || field.fieldType === 'currency') {
          const parsed = Number(raw);
          return {
            ...base,
            valueNumber: Number.isFinite(parsed) ? parsed : null,
          };
        }
        if (field.fieldType === 'date') {
          const parsed = new Date(raw);
          return {
            ...base,
            valueDate: Number.isNaN(parsed.getTime()) ? null : parsed.toISOString(),
          };
        }
        if (field.fieldType === 'boolean') {
          const normalized = raw.toLowerCase();
          return {
            ...base,
            valueBoolean: normalized === 'true' ? true : normalized === 'false' ? false : null,
          };
        }
        if (field.fieldType === 'multi_select') {
          const values = raw
            .split(',')
            .map((value) => value.trim())
            .filter((value) => value.length > 0);
          return {
            ...base,
            valueJson: values.length > 0 ? JSON.stringify(values) : null,
          };
        }
        return {
          ...base,
          valueText: raw,
        };
      });
      return api.updateCrmContactFields(selectedContact.id, { values });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['crm-contacts-query'] }),
        queryClient.invalidateQueries({ queryKey: ['crm-contact-tasks', selectedContact?.id] }),
      ]);
    },
  });

  const createTaskMutation = useMutation({
    mutationFn: async (payload: {
      title: string;
      priority: TaskPriority;
      dueAt: string | null;
    }) => {
      if (!selectedContact) throw new Error('No contact selected');
      const statuses = taskStatuses;
      const defaultStatus = statuses.find((status) => status.key === 'todo') ?? statuses[0];
      return api.createTask({
        title: payload.title,
        priority: payload.priority,
        dueAt: payload.dueAt,
        contactId: selectedContact.id,
        statusId: defaultStatus?.id,
      });
    },
    onSuccess: async () => {
      setNewTaskTitle('');
      setNewTaskDueAt('');
      setNewTaskPriority('medium');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['crm-contact-tasks', selectedContact?.id] }),
        queryClient.invalidateQueries({ queryKey: ['crm-tasks-board'] }),
      ]);
    },
  });

  const moveStageMutation = useMutation({
    mutationFn: async (payload: { contactId: string; stageId: string }) => {
      if (!pipelineId) throw new Error('No active pipeline selected');
      return api.moveCrmContactStage(payload.contactId, {
        pipelineId,
        stageId: payload.stageId,
      });
    },
    onSuccess: async (response, payload) => {
      await queryClient.invalidateQueries({ queryKey: ['crm-contacts-query'] });
      const suggestion = response.data.suggestion;
      if (!suggestion) return;
      const shouldCreate = window.confirm(
        `Create follow-up task?\n\n${suggestion.title}\nPriority: ${suggestion.priority}`,
      );
      if (!shouldCreate) return;
      await api.createTask({
        contactId: payload.contactId,
        title: suggestion.title,
        priority: suggestion.priority,
        dueAt: suggestion.dueAt,
      });
      await queryClient.invalidateQueries({ queryKey: ['crm-contact-tasks', payload.contactId] });
    },
  });

  const isAdmin = meQuery.data?.role === 'admin';
  const isLoading =
    fieldsQuery.isLoading ||
    viewsQuery.isLoading ||
    pipelinesQuery.isLoading ||
    contactsQuery.isLoading;

  if (
    fieldsQuery.isError ||
    viewsQuery.isError ||
    pipelinesQuery.isError ||
    contactsQuery.isError
  ) {
    const error =
      (fieldsQuery.error as Error | undefined)?.message ??
      (viewsQuery.error as Error | undefined)?.message ??
      (pipelinesQuery.error as Error | undefined)?.message ??
      (contactsQuery.error as Error | undefined)?.message ??
      'CRM V2 API is unavailable.';

    return (
      <section className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-xl font-semibold text-foreground">CRM V2 Unavailable</h2>
        <p className="mt-2 text-sm text-muted-foreground">{error}</p>
        <p className="mt-2 text-xs text-muted-foreground">
          Enable backend flag <code>CRM_V2_ENABLED=true</code> and refresh.
        </p>
      </section>
    );
  }

  const stageColumns = board;

  return (
    <div className="space-y-4">
      <header className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-2xl font-semibold text-foreground">Contacts CRM</h2>
            <p className="text-sm text-muted-foreground">
              Dynamic fields, smart views, and pipeline execution in one workspace.
            </p>
          </div>

          <div className="inline-flex rounded-lg border border-border bg-background p-1">
            <button
              type="button"
              className={`rounded-md px-3 py-1 text-sm ${
                viewMode === 'list' ? 'bg-brand-500 text-white' : 'text-muted-foreground'
              }`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
            <button
              type="button"
              className={`rounded-md px-3 py-1 text-sm ${
                viewMode === 'board' ? 'bg-brand-500 text-white' : 'text-muted-foreground'
              }`}
              onClick={() => setViewMode('board')}
            >
              Board
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-6">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search contacts"
            className="bg-background xl:col-span-2"
          />

          <select
            value={pipelineId ?? ''}
            onChange={(event) => setPipelineId(event.target.value || null)}
            className="h-10 rounded-xl border border-border bg-background px-3 text-sm text-foreground"
          >
            {pipelines.map((pipeline) => (
              <option key={pipeline.id} value={pipeline.id}>
                {pipeline.name}
              </option>
            ))}
          </select>

          <select
            value={sortField}
            onChange={(event) => setSortField(event.target.value)}
            className="h-10 rounded-xl border border-border bg-background px-3 text-sm text-foreground"
          >
            <option value="updatedAt">Last Updated</option>
            <option value="displayName">Name</option>
            <option value="lastOutboundAt">Last Outbound</option>
            {fields.map((field) => (
              <option key={field.id} value={field.key}>
                {field.label}
              </option>
            ))}
          </select>

          <select
            value={sortDirection}
            onChange={(event) => setSortDirection(event.target.value === 'asc' ? 'asc' : 'desc')}
            className="h-10 rounded-xl border border-border bg-background px-3 text-sm text-foreground"
          >
            <option value="desc">Sort Desc</option>
            <option value="asc">Sort Asc</option>
          </select>

          <Button variant="secondary" onClick={() => setColumnsOpen((value) => !value)}>
            {columnsOpen ? 'Hide Columns' : 'Columns'}
          </Button>
        </div>

        <div className="mt-3 grid gap-3 md:grid-cols-4">
          <select
            value={filterField}
            onChange={(event) => setFilterField(event.target.value)}
            className="h-10 rounded-xl border border-border bg-background px-3 text-sm text-foreground"
          >
            <option value="stageLabel">Stage</option>
            <option value="decayLevel">Decay</option>
            <option value="displayName">Name</option>
            {fields.map((field) => (
              <option key={field.id} value={field.key}>
                {field.label}
              </option>
            ))}
          </select>
          <select
            value={filterOperator}
            onChange={(event) => setFilterOperator(event.target.value as CrmFilterRule['operator'])}
            className="h-10 rounded-xl border border-border bg-background px-3 text-sm text-foreground"
          >
            <option value="eq">Equals</option>
            <option value="contains">Contains</option>
            <option value="neq">Not equals</option>
            <option value="is_empty">Is empty</option>
            <option value="is_not_empty">Not empty</option>
          </select>
          <Input
            value={filterValue}
            onChange={(event) => setFilterValue(event.target.value)}
            placeholder="Filter value"
            className="bg-background"
          />
          <Button variant="secondary" onClick={() => setFilterValue('')}>
            Clear Filter
          </Button>
        </div>

        {columnsOpen ? (
          <div className="mt-3 rounded-lg border border-border bg-background/70 p-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Visible columns
            </p>
            <div className="flex flex-wrap gap-3">
              {STANDARD_COLUMNS.map((column) => {
                const checked = visibleColumns.includes(column.key);
                return (
                  <label
                    key={column.key}
                    className="inline-flex items-center gap-2 text-sm text-foreground"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-brand-500"
                      checked={checked}
                      onChange={(event) => {
                        setVisibleColumns((current) => {
                          if (event.target.checked) {
                            return [...new Set([...current, column.key])];
                          }
                          return current.filter((value) => value !== column.key);
                        });
                      }}
                    />
                    {column.label}
                  </label>
                );
              })}
              {fields.map((field) => {
                const token = `cf:${field.id}`;
                const checked = visibleColumns.includes(token);
                return (
                  <label
                    key={field.id}
                    className="inline-flex items-center gap-2 text-sm text-foreground"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-brand-500"
                      checked={checked}
                      onChange={(event) => {
                        setVisibleColumns((current) => {
                          if (event.target.checked) {
                            return [...new Set([...current, token])];
                          }
                          return current.filter((value) => value !== token);
                        });
                      }}
                    />
                    {field.label}
                  </label>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="mt-4 rounded-lg border border-border bg-background/70 p-3">
          <div className="flex flex-wrap items-center gap-2">
            {views.map((view) => (
              <button
                key={view.id}
                type="button"
                onClick={() => setActiveViewId(view.id)}
                className={`rounded-full border px-3 py-1 text-xs ${
                  activeViewId === view.id
                    ? 'border-brand-500 bg-brand-500/10 text-brand-700 dark:text-brand-300'
                    : 'border-border text-muted-foreground'
                }`}
              >
                {view.name}
              </button>
            ))}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Input
              value={newViewName}
              onChange={(event) => setNewViewName(event.target.value)}
              placeholder="New smart view name"
              className="max-w-xs bg-background"
            />
            <Button
              variant="secondary"
              onClick={() => saveViewMutation.mutate({ mode: 'create', name: newViewName })}
              isDisabled={saveViewMutation.isPending || !newViewName.trim()}
            >
              Save View
            </Button>
            <Button
              variant="secondary"
              onClick={() => saveViewMutation.mutate({ mode: 'update' })}
              isDisabled={saveViewMutation.isPending || !activeViewId}
            >
              Update Active
            </Button>
            <Button
              variant="secondary"
              onClick={() => deleteViewMutation.mutate()}
              isDisabled={deleteViewMutation.isPending || !activeViewId}
            >
              Delete Active
            </Button>
            {!isAdmin ? (
              <span className="text-xs text-muted-foreground">
                Only admins can share views globally.
              </span>
            ) : null}
          </div>
          {saveViewMutation.isError ? (
            <p className="mt-2 text-xs text-red-500">{(saveViewMutation.error as Error).message}</p>
          ) : null}
          {deleteViewMutation.isError ? (
            <p className="mt-2 text-xs text-red-500">
              {(deleteViewMutation.error as Error).message}
            </p>
          ) : null}
        </div>
      </header>

      <div className="grid gap-4 xl:grid-cols-[1.8fr_1fr]">
        <section className="min-h-[420px] rounded-xl border border-border bg-card p-3">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading CRM contacts...</p>
          ) : viewMode === 'list' ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    {visibleColumns.map((column) => (
                      <th key={column} className="px-3 py-2 font-medium">
                        {resolveColumnLabel(column, fields)}
                      </th>
                    ))}
                    <th className="px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isSelected = row.id === selectedContact?.id;
                    return (
                      <tr
                        key={row.id}
                        onClick={() => {
                          const nextParams = new URLSearchParams(searchParams);
                          nextParams.set('contact', row.id);
                          setSearchParams(nextParams, { replace: true });
                        }}
                        className={`cursor-pointer border-b border-border/70 ${
                          isSelected ? 'bg-brand-500/5' : 'hover:bg-muted/40'
                        }`}
                      >
                        {visibleColumns.map((column) => {
                          if (column === 'decayLevel') {
                            return (
                              <td key={column} className="px-3 py-2">
                                <span
                                  className={`rounded-full px-2 py-0.5 text-xs ${getDecayBadgeClass(row.decayLevel)}`}
                                >
                                  {row.decayLevel}
                                </span>
                              </td>
                            );
                          }
                          return (
                            <td key={column} className="px-3 py-2 text-foreground">
                              {getCellValue(row, column, fields)}
                            </td>
                          );
                        })}
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            className="rounded border border-border px-2 py-1 text-xs text-foreground"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(
                                buildDialerPath({
                                  contact: row.id,
                                  phone: row.phoneNumber,
                                  action: 'call',
                                }),
                              );
                            }}
                          >
                            Call
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={visibleColumns.length + 1}
                        className="px-3 py-8 text-center text-muted-foreground"
                      >
                        No contacts matched the current view.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="grid gap-3 overflow-x-auto md:grid-cols-2 xl:grid-cols-4">
              {stageColumns.map((column) => (
                <div
                  key={column.stage.id}
                  className="min-h-[260px] rounded-lg border border-border bg-background/70 p-2"
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    const contactId = event.dataTransfer.getData('text/contact-id');
                    if (!contactId) return;
                    moveStageMutation.mutate({ contactId, stageId: column.stage.id });
                  }}
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold text-foreground">{column.stage.label}</h3>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      {column.contacts.length}
                    </span>
                  </div>

                  <div className="space-y-2">
                    {column.contacts.map((contact) => (
                      <article
                        key={contact.id}
                        draggable
                        onDragStart={(event) => {
                          event.dataTransfer.setData('text/contact-id', contact.id);
                        }}
                        onClick={() => {
                          const nextParams = new URLSearchParams(searchParams);
                          nextParams.set('contact', contact.id);
                          setSearchParams(nextParams, { replace: true });
                        }}
                        className="cursor-move rounded-lg border border-border bg-card p-2"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {contact.displayName ?? contact.phoneNumber}
                            </p>
                            <p className="text-xs text-muted-foreground">{contact.phoneNumber}</p>
                          </div>
                          <button
                            type="button"
                            className="rounded border border-border px-2 py-0.5 text-xs"
                            onClick={(event) => {
                              event.stopPropagation();
                              navigate(
                                buildDialerPath({
                                  contact: contact.id,
                                  phone: contact.phoneNumber,
                                  action: 'call',
                                }),
                              );
                            }}
                          >
                            Call
                          </button>
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] ${getDecayBadgeClass(contact.decayLevel)}`}
                          >
                            {contact.decayLevel}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {formatDateLabel(contact.lastOutboundAt ?? null)}
                          </span>
                        </div>
                      </article>
                    ))}
                    {column.contacts.length === 0 ? (
                      <p className="rounded border border-dashed border-border px-2 py-4 text-center text-xs text-muted-foreground">
                        Drop contacts here
                      </p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <aside className="rounded-xl border border-border bg-card p-4">
          {selectedContact ? (
            <>
              <div className="mb-3 flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {selectedContact.displayName ?? selectedContact.phoneNumber}
                  </h3>
                  <p className="text-xs text-muted-foreground">{selectedContact.phoneNumber}</p>
                </div>
                <button
                  type="button"
                  className="rounded border border-border px-2 py-1 text-xs"
                  onClick={() =>
                    navigate(
                      buildDialerPath({
                        contact: selectedContact.id,
                        phone: selectedContact.phoneNumber,
                        action: 'call',
                      }),
                    )
                  }
                >
                  Call Now
                </button>
              </div>

              <div className="mb-3 inline-flex rounded-lg border border-border bg-background p-1">
                {(['profile', 'fields', 'tasks'] as DetailTab[]).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setDetailTab(tab)}
                    className={`rounded-md px-2.5 py-1 text-xs ${
                      detailTab === tab ? 'bg-brand-500 text-white' : 'text-muted-foreground'
                    }`}
                  >
                    {tab === 'profile' ? 'Profile' : tab === 'fields' ? 'Custom Fields' : 'Tasks'}
                  </button>
                ))}
              </div>

              {detailTab === 'profile' ? (
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="text-xs text-muted-foreground">Email</dt>
                    <dd className="text-foreground">{selectedContact.email ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Stage</dt>
                    <dd className="text-foreground">{selectedContact.stageLabel ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Last Outbound</dt>
                    <dd className="text-foreground">
                      {formatDateLabel(selectedContact.lastOutboundAt ?? null)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Last Inbound</dt>
                    <dd className="text-foreground">
                      {formatDateLabel(selectedContact.lastInboundAt ?? null)}
                    </dd>
                  </div>
                </dl>
              ) : null}

              {detailTab === 'fields' ? (
                <div className="space-y-3">
                  {fields.map((field) => {
                    const draftValue = customFieldDrafts[field.id] ?? '';
                    if (field.fieldType === 'dropdown' || field.fieldType === 'multi_select') {
                      return (
                        <div key={field.id}>
                          <label className="mb-1 block text-xs text-muted-foreground">
                            {field.label}
                          </label>
                          <select
                            value={draftValue}
                            onChange={(event) =>
                              setCustomFieldDrafts((current) => ({
                                ...current,
                                [field.id]: event.target.value,
                              }))
                            }
                            className="h-10 w-full rounded-xl border border-border bg-background px-3 text-sm"
                          >
                            <option value="">No value</option>
                            {(field.options ?? []).map((option) => (
                              <option key={option.id} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    }

                    if (field.fieldType === 'boolean') {
                      return (
                        <div key={field.id}>
                          <label className="mb-1 block text-xs text-muted-foreground">
                            {field.label}
                          </label>
                          <select
                            value={draftValue}
                            onChange={(event) =>
                              setCustomFieldDrafts((current) => ({
                                ...current,
                                [field.id]: event.target.value,
                              }))
                            }
                            className="h-10 w-full rounded-xl border border-border bg-background px-3 text-sm"
                          >
                            <option value="">No value</option>
                            <option value="true">True</option>
                            <option value="false">False</option>
                          </select>
                        </div>
                      );
                    }

                    const inputType =
                      field.fieldType === 'date'
                        ? 'date'
                        : field.fieldType === 'number' || field.fieldType === 'currency'
                          ? 'number'
                          : 'text';

                    return (
                      <div key={field.id}>
                        <label className="mb-1 block text-xs text-muted-foreground">
                          {field.label}
                        </label>
                        <Input
                          value={draftValue}
                          onChange={(event) =>
                            setCustomFieldDrafts((current) => ({
                              ...current,
                              [field.id]: event.target.value,
                            }))
                          }
                          type={inputType}
                          className="bg-background"
                        />
                      </div>
                    );
                  })}

                  <Button
                    variant="secondary"
                    onClick={() => updateFieldsMutation.mutate()}
                    isDisabled={updateFieldsMutation.isPending}
                  >
                    {updateFieldsMutation.isPending ? 'Saving...' : 'Save Custom Fields'}
                  </Button>
                  {updateFieldsMutation.isError ? (
                    <p className="text-xs text-red-500">
                      {(updateFieldsMutation.error as Error).message}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {detailTab === 'tasks' ? (
                <div className="space-y-3">
                  <div className="rounded-lg border border-border bg-background/70 p-3">
                    <Input
                      value={newTaskTitle}
                      onChange={(event) => setNewTaskTitle(event.target.value)}
                      placeholder="New follow-up task"
                      className="bg-background"
                    />
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <select
                        value={newTaskPriority}
                        onChange={(event) => setNewTaskPriority(event.target.value as TaskPriority)}
                        className="h-10 rounded-xl border border-border bg-background px-3 text-sm"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                      <Input
                        type="datetime-local"
                        value={newTaskDueAt}
                        onChange={(event) => setNewTaskDueAt(event.target.value)}
                        className="bg-background"
                      />
                    </div>
                    <Button
                      className="mt-2"
                      variant="secondary"
                      onClick={() =>
                        createTaskMutation.mutate({
                          title: newTaskTitle.trim(),
                          priority: newTaskPriority,
                          dueAt: newTaskDueAt ? new Date(newTaskDueAt).toISOString() : null,
                        })
                      }
                      isDisabled={createTaskMutation.isPending || !newTaskTitle.trim()}
                    >
                      {createTaskMutation.isPending ? 'Adding...' : 'Add Task'}
                    </Button>
                    {createTaskMutation.isError ? (
                      <p className="mt-2 text-xs text-red-500">
                        {(createTaskMutation.error as Error).message}
                      </p>
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    {(selectedContactTasksQuery.data ?? []).map((task: Task) => {
                      const status = taskStatuses.find(
                        (statusRow) => statusRow.id === task.statusId,
                      );
                      return (
                        <div
                          key={task.id}
                          className="rounded-lg border border-border bg-background/70 p-3"
                        >
                          <p className="text-sm font-medium text-foreground">{task.title}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span className="rounded-full border border-border px-2 py-0.5">
                              {task.priority}
                            </span>
                            <span>{status?.label ?? 'Unknown status'}</span>
                            <span>Due: {formatDateLabel(task.dueAt)}</span>
                          </div>
                        </div>
                      );
                    })}
                    {selectedContactTasksQuery.isLoading ? (
                      <p className="text-xs text-muted-foreground">Loading tasks...</p>
                    ) : null}
                    {selectedContactTasksQuery.data?.length === 0 ? (
                      <p className="text-xs text-muted-foreground">
                        No tasks yet for this contact.
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Select a contact to see details.</p>
          )}
        </aside>
      </div>

      {moveStageMutation.isError ? (
        <p className="text-sm text-red-500">{(moveStageMutation.error as Error).message}</p>
      ) : null}
    </div>
  );
}
